import type { ButtonHTMLAttributes } from 'react'
import { CountBadge } from './CountBadge'
import { Icon } from './Icon'
import type { IconName } from './icons'

export type IconButtonVariant = 'ghost' | 'neutral' | 'primary'

export interface IconButtonProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'children' | 'aria-label'
> {
  icon: IconName
  /** Required accessible name (there is no visible text). Include the badge count when relevant. */
  label: string
  variant?: IconButtonVariant
  /** md = 44px (default), lg = 48px. */
  size?: 'md' | 'lg'
  /** Toggle button (e.g. favorite) → aria-pressed. */
  pressed?: boolean
  loading?: boolean
  badge?: number | 'dot'
}

/** Icon-only button. Touch target ≥ 44px; the name never depends on a tooltip. */
export function IconButton({
  icon,
  label,
  variant = 'ghost',
  size = 'md',
  pressed,
  loading = false,
  badge,
  className,
  disabled,
  type,
  ...rest
}: IconButtonProps) {
  const classes = [
    'ui-icon-button',
    `ui-icon-button--${variant}`,
    `ui-icon-button--${size}`,
    className,
  ]
  return (
    <button
      type={type ?? 'button'}
      className={classes.filter(Boolean).join(' ')}
      aria-label={label}
      aria-pressed={pressed}
      aria-busy={loading || undefined}
      disabled={disabled || loading}
      {...rest}
    >
      <Icon
        name={loading ? 'loading' : icon}
        size={20}
        {...(loading ? { className: 'ui-spin' } : {})}
      />
      {badge !== undefined && (
        <CountBadge
          {...(badge === 'dot' ? { dot: true } : { count: badge })}
          className="ui-icon-button__badge"
        />
      )}
    </button>
  )
}
